export type LifecycleStage = "emerging" | "growing" | "mature" | "declining";

export interface Keyword {
  id: number;
  keyword: string;
  channel_id: number;
  score: number;
  normalized_score: number | null;
  mention_count: number;            // 30일 누적 멘션
  mention_count_7d?: number;        // 룰 v4 (2026-05-07) — 7일 누적 멘션, emerging 카드 핵심 시그널
  lifecycle_stage: LifecycleStage | null;
  date: string;
  // 실속 지표 (있을 때만 노출)
  shopping_count?: number | null;
  datalab_direction?: "rising" | "flat" | "falling" | null;
  score_change_pct?: number | null;
  mention_change_per_day?: number | null;   // 일평균 mention 차이 (avg7 - avg30, 소수 1자리). 정렬·표시 기준 (사용자 결정 2026-05-02)
  // 연계 시그널 (F-3 종료 후)
  recipe_count?: number;
  place_count?: number;
  manufacturer_count?: number;
  // β-9 (2026-04-29): 30일 롤링 윈도우 메타
  is_new?: boolean;                 // first_seen_date <= 7일 → 🆕 신규 배지
  first_seen_date?: string | null;
  days_active?: number | null;      // 30일 윈도우 내 등장 일수
  // β-9 후속 (D 항목, 2026-04-29): declining 세분화
  // 'bubble' = 🫧 거품 단계 (정규화≥50 + declining), 'fading' = 📉 일반 하락 (정규화<50)
  lifecycle_substage?: "bubble" | "fading" | null;
  // 마이그 081 (2026-05-18) — 시즌 자동 분류 (peak/others_avg >= 1.8)
  is_seasonal?: boolean;
  seasonal_peak_months?: string | null;   // 예: "6-8" / "11-12" (annual 용)
  // 마이그 087 (2026-05-20) — annual (매해 반복) vs one_off (단발 폭발) 구분
  seasonal_type?: "annual" | "one_off" | null;
  seasonal_peak_period?: string | null;   // one_off 용 'YYYY-MM' (예: "2026-01")
  // 사용자 결정 2026-05-05 — 검색 모드 응답에만 채움. 전체 풀 안 점수 desc 순위
  overall_rank?: number | null;
}

export interface SavedKeyword {
  id: number;
  keyword_text: string;
  channel_id: number;
  created_at: string;
  keyword_id: number | null;
  score: number | null;
  lifecycle_stage: LifecycleStage | null;
  mention_count: number | null;
}

export interface NotificationPref {
  kakao_enabled: boolean;
  email_enabled: boolean;
  telegram_enabled: boolean;
  email: string | null;
  telegram_chat_id: string | null;
  phone: string | null;   // 마이그 117 (2026-06-02) — 카카오 알림톡·SMS 폴백
  kakao_channel_url?: string | null;   // 2026-06-02 — 카카오 채널 친구추가 deep link
  kakao_alimtalk_available?: boolean;   // 2026-06-02 — 알리고 회신 대기 중 일시 비활성
  daily_digest: boolean;
  saved_alert: boolean;
  stock_alert: boolean;
  mention_alert: boolean;          // R3-33 S4-D
  weekly_digest: boolean;          // R3-33 S4-F
  digest_categories: string[];     // 마이그 030 — 추가 관심 카테고리
  digest_frequency?: "weekly" | "daily";   // 마이그 040 — 발송 빈도
  level_up_notify: boolean;        // 마이그 037 — 활동 등급 승급 알림
  // Phase 2.5 (2026-05-13, 마이그 058) — 일상 뉴스 락인 digest 옵트인
  daily_news_digest: boolean;
  daily_news_categories: string[];
  // 마이그 076 (2026-05-15) — 광고·마케팅 수신 동의 (정통망법)
  marketing_consent: boolean;
}

export type PostType = "free" | "item_tagged" | "poll" | "market";
// 마이그 105 — 중고장터
export type TradeType = "sell" | "buy";
export type PriceType = "fixed" | "free" | "negotiable";
export type ItemCondition = "new" | "like_new" | "used";
export type TradeMethod = "direct" | "delivery" | "both";
export type MarketStatus = "selling" | "reserved" | "sold";

export interface CommunityInsight {
  menu_master_id: number;
  menu_name: string | null;
  metric_type: string;     // revenue | qty | rating | other
  metric_before: number;
  metric_after: number;
  change_pct: number | null;
}

export interface CommunityPoll {
  pro_count: number;
  con_count: number;
  total: number;
  closes_at: string | null;
  is_closed: boolean;
  my_side: "pro" | "con" | null;
}

export interface CommunityPostOut {
  id: number;
  user_id: number;
  author_name: string | null;     // R2-4 — 닉네임 (없으면 "익명")
  // 마이그 035 — 활동 등급
  author_level?: number | null;
  author_label?: string | null;
  author_emoji?: string | null;
  author_is_admin?: boolean;       // #11 — 운영자 배지
  post_type: PostType;            // R2-5 자유/태그 + R3-33 찬반(poll)
  title: string | null;           // R2-5 — 자유글 제목 / poll 질문 (필수)
  keyword_id: number | null;      // R2-5 — free 글이면 null
  keyword: string | null;         // R2-5
  content: string;
  report_count: number;
  created_at: string;
  updated_at: string;
  comment_count: number;
  like_count: number;
  liked: boolean;
  // R3-33 S4-D
  view_count?: number;
  region?: string | null;
  category?: string | null;
  insight?: CommunityInsight | null;
  // R3-33 사용자 피드백 (2026-04-28)
  poll?: CommunityPoll | null;
  // 마이그 036 — 글 내용 카테고리
  content_category?: ContentCategoryKey | null;
  // 마이그 039 — 첨부 이미지 URL (최대 5장)
  images?: string[];
  // 마이그 101 (2026-05-25) — 본인이 ⭐ 북마크한 글 여부
  bookmarked?: boolean;
  // 마이그 105 — 중고장터 (post_type='market')
  trade_type?: TradeType | null;
  market_category?: string | null;
  price?: number | null;
  price_type?: PriceType | null;
  item_condition?: ItemCondition | null;
  trade_method?: TradeMethod | null;
  market_status?: MarketStatus | null;
}

export interface CommunityCommentOut {
  id: number;
  user_id: number;
  author_name: string | null;     // R2-4
  // 마이그 035
  author_level?: number | null;
  author_label?: string | null;
  author_emoji?: string | null;
  author_is_admin?: boolean;       // #11 — 운영자 배지
  content: string;
  parent_comment_id: number | null;
  created_at: string;
  // 마이그 038 — 댓글 좋아요 + 베스트
  like_count?: number;
  liked?: boolean;
  best_rank?: number | null;     // 1~3 = 베스트 TOP 3
}

export interface CommunityPostDetail extends CommunityPostOut {
  comments: CommunityCommentOut[];
}

export interface TrendListOut {
  date: string;
  is_fallback: boolean;
  total: number;
  tier: string;
  limit: number;
  keywords: Keyword[];
}

export interface TrendListPaginated {
  date: string;
  is_fallback: boolean;
  total: number;
  tier: string;
  max_visible: number | null;
  page: number;
  size: number;
  has_more: boolean;
  locked: boolean;
  keywords: Keyword[];
}

export interface StagesSummary {
  date: string;
  is_fallback: boolean;
  stages: Record<string, number>;
  stages_new?: Record<string, number>;   // 사용자 결정 2026-05-03 — 이번주 신규 카운트
}

export interface UserMe {
  id: number;
  email: string;
  nickname: string | null;        // R2-4 — 커뮤니티 표시명
  tier: string;
  is_beta: boolean;
  is_admin: boolean;
  kakao_id: string | null;
  login_type: string;
  // 마이그 035 — 활동 점수 + 등급 (1~5)
  activity_score?: number;
  activity_level?: number;
  activity_label?: string;
  activity_emoji?: string;
  // 마이그 093 — 프랜차이즈 역할
  franchise_role?: "hq" | "member" | null;
  // 옵션 C (2026-05-25) — 본사 결제 inherit 반영한 효과적 등급 (베타 종료 후 의미)
  // 베타 동안엔 tier 와 동일하지만 베타 종료 후 가맹점이 본사 hq_pays_all + 결제 OK 면 'paid' 반환
  effective_tier?: string;
}

export const ACTIVITY_LEVELS = [
  // 2026-05-19 — 임계 4배 상향 (사용자 결정). 옛 50/200/600/1500 → 200/800/2500/6000
  { level: 1, label: "새내기", emoji: "☕", min: 0,    color: "#9CA3AF" },
  { level: 2, label: "단골",   emoji: "🍰", min: 200,  color: "#60A5FA" },
  { level: 3, label: "VIP",    emoji: "🌟", min: 800,  color: "#FBBF24" },
  { level: 4, label: "사장님", emoji: "👑", min: 2500, color: "#F472B6" },
  { level: 5, label: "명장",   emoji: "🏆", min: 6000, color: "#FB7185" },
] as const;

export type ContentCategoryKey = "insight" | "recipe" | "operation" | "new_menu" | "casual" | "question";

export interface ContentCategoryStat {
  key: ContentCategoryKey;
  label: string;
  desc: string;
  count: number;
}

export const STAGE_LABEL: Record<LifecycleStage, string> = {
  emerging: "태동기",
  growing: "성장기",
  mature: "성숙기",
  declining: "하락기",
};

export const STAGE_EMOJI: Record<LifecycleStage, string> = {
  emerging: "🌱",
  growing: "🔥",
  mature: "📊",
  declining: "📉",
};

export const STAGE_COLOR: Record<LifecycleStage, string> = {
  emerging: "#4ADE80",
  growing: "#FF5C35",
  mature: "#60A5FA",
  declining: "#6B7280",
};

export const STAGE_BG: Record<LifecycleStage, string> = {
  emerging: "rgba(74,222,128,0.15)",
  growing: "rgba(255,92,53,0.15)",
  mature: "rgba(96,165,250,0.15)",
  declining: "rgba(107,114,128,0.15)",
};

export interface HistoryPoint {
  date: string;
  score: number;
  mention_count: number;
}

export interface RecipePreview {
  id: number;
  title: string;
  youtube_url: string;
  channel_name: string | null;
  view_count: number | null;
  difficulty: string | null;
  is_exact_match?: boolean;   // 1번 (2026-05-03) — 제목에 키워드 정확 포함 여부
}

export interface KeywordDetail {
  keyword: Keyword;
  recipes: RecipePreview[];
  // place_summary 폐기 (사용자 결정 2026-05-09 — Place 시스템 통째 폐기). 백엔드는 호환 위해 빈값 반환 중.
  community_preview: { id: number; content: string; created_at: string }[];
}
